import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../../config/Supabase";
import SEO from "../utils/SEO";
import SocialShare from "../utils/SocialShare";
import { getQuiz } from "../../data/quizzes";
import { getQuizIcon } from "./quizIcons";
import { getHerbCategory } from "../../data/herbCategories";
import { herbSlug } from "../../utils/herbSlug";
import { trackCTAClick } from "../../utils/analytics";
import "../../styles/Quiz.css";
import "../../styles/HerbCategories.css";

const truncate = (t, n) => (t && t.length > n ? `${t.slice(0, n)}…` : t || "");

// Shareable, indexable result page for any engine quiz. Renders the matched
// result, recommended herbs, a fail-soft grid of real herbs from the linked
// category, and the conversion CTA into the /start trial funnel. Wellness framing
// only — every page carries the non-diagnostic + not-medical-advice disclaimers.
const QuizResult = () => {
  const { slug, type } = useParams();
  const def = getQuiz(slug);
  const result = def ? def.results[type] : null;
  const category = result ? getHerbCategory(result.categorySlug) : null;

  const [herbs, setHerbs] = useState([]);

  // Pull a few real herbs from the matched category (mirrors HerbCategoryList's
  // .overlaps query). Fail-soft: if it errors or returns nothing, the section
  // simply doesn't render.
  useEffect(() => {
    if (!category) return;
    let active = true;
    (async () => {
      const subs = (category.subcategories || []).map((s) => s.toLowerCase());
      const { data } = await supabase
        .from("herbs")
        .select("id,title,summary,img_url")
        .overlaps("therapeutic_actions", subs)
        .order("popularity_score", { ascending: false, nullsFirst: false })
        .limit(4);
      if (active) setHerbs(data || []);
    })();
    return () => {
      active = false;
    };
  }, [category]);

  if (!def || !result) {
    return (
      <div className="quiz-result" style={{ textAlign: "center" }}>
        <SEO title="Quiz results | Sarv" noIndex={true} />
        <h1 className="quiz-result__name">Result not found</h1>
        <p className="quiz-result__summary">
          Try one of our <Link to="/quizzes">wellness quizzes →</Link>
        </p>
      </div>
    );
  }

  const ctaSource = `${def.slug}-${type}`;
  const title = `You're ${result.name} ${result.emoji} — ${def.title} | Sarv`;
  const description = `${result.tagline} ${result.summary}`.slice(0, 300);

  return (
    <div className="quiz-result">
      <SEO
        title={title}
        description={description}
        canonicalPath={`/quiz/${def.slug}/result/${type}`}
        keywords={def.keywords}
      />

      <div className="quiz-result__head">
        {(() => {
          const Icon = getQuizIcon(def.slug);
          return (
            <span className="quiz-result__icon" aria-hidden="true">
              <Icon />
            </span>
          );
        })()}
        <p className="quiz-eyebrow">{def.title}</p>
        <h1 className="quiz-result__name">{result.name}</h1>
        <p className="quiz-result__tagline">{result.tagline}</p>
      </div>

      <p className="quiz-result__summary">{result.summary}</p>

      {result.focus?.length > 0 && (
        <>
          <h2 className="quiz-result__h2">Where to focus</h2>
          <ul className="quiz-result__focus">
            {result.focus.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </>
      )}

      {result.herbs?.length > 0 && (
        <>
          <h2 className="quiz-result__h2">Herbs traditionally used</h2>
          <div className="quiz-herbs">
            {result.herbs.map((h, i) => (
              <span key={i} className="quiz-herb-chip">
                {h}
              </span>
            ))}
          </div>
        </>
      )}

      {herbs.length > 0 && (
        <section className="quiz-result__browse">
          <h2 className="quiz-result__h2">
            {category ? `Explore ${category.name} herbs` : "Explore related herbs"}
          </h2>
          <p className="quiz-result__browsehint">
            A selection from our{" "}
            {category ? category.name.toLowerCase() : "herb"} library — tap any herb to see how
            it's traditionally used, its benefits, and safety notes.
          </p>
          <div className="quiz-hub__grid">
            {herbs.map((h) => (
              <Link
                key={h.id}
                to={`/herbs/${herbSlug(h)}`}
                className="herbcat-card"
                style={{ textDecoration: "none" }}
              >
                <div className="herbcat-card__imgwrap">
                  <img src={h.img_url} alt={h.title} loading="lazy" />
                </div>
                <div style={{ padding: "14px 16px" }}>
                  <div className="herbcat-card__title">{h.title}</div>
                  <div className="herbcat-card__desc">{truncate(h.summary, 90)}</div>
                </div>
              </Link>
            ))}
          </div>
          {category && (
            <p className="quiz-result__explore">
              <Link to={`/herbs/category/${category.slug}`}>
                Explore all {category.name} herbs →
              </Link>
            </p>
          )}
        </section>
      )}

      <div className="quiz-cta">
        <p className="quiz-cta__title">See exactly what to do next</p>
        <p className="quiz-cta__sub">
          Sarv turns your results into a personalized plan — the right herbs, routine, and daily
          habits matched to your body and your goals.
        </p>
        <Link
          to={`/start?source=${ctaSource}`}
          className="quiz-btn quiz-btn--primary"
          style={{ textDecoration: "none" }}
          onClick={() => trackCTAClick("quiz_result_start_trial", ctaSource)}
        >
          Get my personalized plan →
        </Link>
      </div>

      <div className="quiz-share">
        <p>Share your result</p>
        <SocialShare />
      </div>

      <div style={{ textAlign: "center", marginTop: 20 }}>
        <Link to={`/quiz/${def.slug}`} className="quiz-btn quiz-btn--ghost" style={{ textDecoration: "none" }}>
          Retake the quiz
        </Link>
      </div>

      <p className="quiz-result__disclaimer">
        This is a general wellness self-assessment, not a medical diagnosis. The information here
        is for educational purposes only and is not a substitute for professional medical advice.
        Consult a qualified healthcare provider before using any herb — especially if you are
        pregnant, nursing, or taking medication.
        {def.disclaimer ? ` ${def.disclaimer}` : ""}
      </p>
    </div>
  );
};

export default QuizResult;
